<script setup>
import { computed, ref, watch } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Policy from 'dashboard/components/policy.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { INBOX_TYPES, getInboxIconByType } from 'dashboard/helper/inbox';
import {
  AI_AGENT_MODE_OPTIONS,
  AUTONOMOUS_MODES,
  DEFAULT_AI_AGENT_MODE,
  aiAgentModeDescription,
} from 'dashboard/helper/aiAgentMode';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  inbox: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['action', 'modeChange']);

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const { isAdmin } = useAdmin();

const [showActionsDropdown, toggleDropdown] = useToggle();

const inboxName = computed(() => {
  const inbox = props.inbox;
  if (!inbox?.name) {
    return '';
  }

  const isTwilioChannel = inbox.channel_type === INBOX_TYPES.TWILIO;
  const isWhatsAppChannel = inbox.channel_type === INBOX_TYPES.WHATSAPP;
  const isEmailChannel = inbox.channel_type === INBOX_TYPES.EMAIL;

  if (isTwilioChannel || isWhatsAppChannel) {
    const identifier = inbox.messaging_service_sid || inbox.phone_number;
    return identifier ? `${inbox.name} (${identifier})` : inbox.name;
  }

  if (isEmailChannel && inbox.email) {
    return `${inbox.name} (${inbox.email})`;
  }

  return inbox.name;
});

const menuItems = computed(() => [
  {
    label: t('CAPTAIN.INBOXES.OPTIONS.DISCONNECT'),
    value: 'delete',
    action: 'delete',
    icon: 'i-ri-delete-bin-line',
  },
]);

const icon = computed(() => {
  const { medium, channel_type: type } = props.inbox;
  return getInboxIconByType(type, medium, 'outline');
});

const handleAction = ({ action, value }) => {
  toggleDropdown(false);
  emit('action', { action, value, id: props.id });
};

// SiteUp: modo da IA POR CAIXA. O modo efetivo vem do backend
// (inbox.captain_ai_mode); ao trocar, o card persiste via store (e emite
// 'modeChange' para quem quiser observar) — assim dá pra ter uma caixa autônoma
// e outra só sugerindo. As opções (valor/rótulo) e o default vêm do helper
// aiAgentMode (fonte única, espelha o backend AI_MODES/DEFAULT_AI_MODE).
const AI_MODE_OPTIONS = AI_AGENT_MODE_OPTIONS;

const mode = ref(props.inbox.captain_ai_mode || DEFAULT_AI_AGENT_MODE);
watch(
  () => props.inbox.captain_ai_mode,
  value => {
    mode.value = value ?? DEFAULT_AI_AGENT_MODE;
  }
);

const modeDescription = computed(() => aiAgentModeDescription(mode.value));

const isSavingMode = ref(false);

// Persiste o modo da IA da caixa. O backend só autoriza admin (AssistantPolicy
// #update? = administrator?), então: (a) o seletor fica desabilitado p/ não-admin
// (não dá pra disparar um 403 silencioso); (b) se a gravação falhar mesmo assim,
// revertemos o seletor para o valor anterior e avisamos — em vez de o select
// "ficar" no valor novo sem nada ter sido salvo. Mantém o emit 'modeChange' para
// quem quiser observar (a persistência mora aqui).
// Ligar um modo que ENVIA ao cliente sem revisão é visível por terceiros:
// pede confirmação com o efeito escrito (regra de UX da casa). Sair de um modo
// autônomo, ou trocar entre modos que só sugerem, salva direto.
const persistirModo = async (previousMode, nextMode) => {
  emit('modeChange', { id: props.inbox.id, mode: nextMode });

  isSavingMode.value = true;
  try {
    await store.dispatch('captainInboxes/updateMode', {
      inboxId: props.inbox.id,
      // route sempre existe em produção; opcional só para o ambiente de stories
      // (Histoire não instala o vue-router) não quebrar ao interagir com o card.
      assistantId: route?.params?.assistantId,
      mode: nextMode,
    });
  } catch (error) {
    mode.value = previousMode;
    useAlert(
      error?.message || 'Não foi possível alterar o modo da IA nesta caixa.'
    );
  } finally {
    isSavingMode.value = false;
  }
};

const confirmaModoDialog = ref(null);
const modoPendente = ref(null);
const precisaConfirmar = (anterior, proximo) =>
  AUTONOMOUS_MODES.includes(proximo) && !AUTONOMOUS_MODES.includes(anterior);

const onModeChange = async () => {
  const previousMode = props.inbox.captain_ai_mode || DEFAULT_AI_AGENT_MODE;
  const nextMode = mode.value;
  if (precisaConfirmar(previousMode, nextMode)) {
    modoPendente.value = { previousMode, nextMode };
    confirmaModoDialog.value?.open();
    return;
  }
  await persistirModo(previousMode, nextMode);
};

const confirmarModo = async () => {
  const pendente = modoPendente.value;
  modoPendente.value = null;
  confirmaModoDialog.value?.close();
  if (pendente) await persistirModo(pendente.previousMode, pendente.nextMode);
};

// Chamado pelo @close do Dialog: ele já está fechando, então NÃO chamar
// close() de volta (close emite close, que chama isto, que chama close: pilha
// estourada, achado do QA da v981).
const cancelarModo = () => {
  if (modoPendente.value) mode.value = modoPendente.value.previousMode;
  modoPendente.value = null;
};

// ── SiteUp (SITE-7): horário de ATUAÇÃO DA IA por caixa ─────────────────────
// Independe do expediente dos atendentes (working_hours). 'follow' = enabled:false
// (segue o expediente nativo = comportamento atual); '24h'/'inside'/'outside' =
// enabled:true. Persiste em config['inbox_ai_hours'][inbox_id] (mesma família de
// API do inbox_modes) e o backend avalia em Captain::AiActiveHoursService (a
// janela pode cruzar a meia-noite).
const HOURS_FOLLOW = 'follow';
const DAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const TIMEZONES = [
  'America/Sao_Paulo',
  'America/Bahia',
  'America/Fortaleza',
  'America/Recife',
  'America/Belem',
  'America/Manaus',
  'America/Cuiaba',
  'America/Rio_Branco',
  'UTC',
];
const HOURS_MODE_OPTIONS = [
  {
    value: HOURS_FOLLOW,
    label: 'Seguir o expediente da caixa',
    description:
      'A IA respeita o horário comercial configurado na caixa (padrão).',
  },
  {
    value: '24h',
    label: '24 horas',
    description: 'A IA atende a qualquer hora.',
  },
  {
    value: 'inside',
    label: 'Só dentro do horário',
    description: 'A IA atende apenas na janela definida abaixo.',
  },
  {
    value: 'outside',
    label: 'Só fora do horário',
    description: 'A IA atende apenas FORA da janela (cobertura after-hours).',
  },
];

const pad2 = n => String(Number(n) || 0).padStart(2, '0');
const toTimeString = (hour, minutes, fallback) => {
  if (hour === null || hour === undefined || hour === '') return fallback;
  return `${pad2(hour)}:${pad2(minutes)}`;
};

const buildDefaultSchedule = () =>
  DAY_LABELS.map((_, wday) => ({
    day_of_week: wday,
    open: wday >= 1 && wday <= 5, // seg–sex aberto por padrão
    openTime: '08:00',
    closeTime: '18:00',
  }));

const scheduleFromConfig = cfg => {
  const rows = Array.isArray(cfg?.schedule) ? cfg.schedule : [];
  if (!rows.length) return buildDefaultSchedule();
  const byWday = {};
  rows.forEach(d => {
    byWday[Number(d.day_of_week)] = d;
  });
  return DAY_LABELS.map((_, wday) => {
    const d = byWday[wday];
    if (!d) {
      return {
        day_of_week: wday,
        open: false,
        openTime: '08:00',
        closeTime: '18:00',
      };
    }
    const closed = d.closed_all_day === true || d.closed_all_day === 'true';
    return {
      day_of_week: wday,
      open: !closed,
      openTime: toTimeString(d.open_hour, d.open_minutes, '08:00'),
      closeTime: toTimeString(d.close_hour, d.close_minutes, '18:00'),
    };
  });
};

const initialHours = props.inbox.captain_ai_hours || {};
const aiHoursMode = ref(
  initialHours.enabled ? initialHours.mode || '24h' : HOURS_FOLLOW
);
const aiTimezone = ref(
  initialHours.timezone || props.inbox.timezone || 'America/Sao_Paulo'
);
const schedule = ref(scheduleFromConfig(initialHours));
const showHours = ref(false);
const isSavingHours = ref(false);

const needsSchedule = computed(() =>
  ['inside', 'outside'].includes(aiHoursMode.value)
);
const hoursSummary = computed(
  () => HOURS_MODE_OPTIONS.find(o => o.value === aiHoursMode.value)?.label || ''
);

const onSaveHours = async () => {
  const enabled = aiHoursMode.value !== HOURS_FOLLOW;
  const aiHours = {
    enabled,
    // backend valida mode em MODES mesmo com enabled:false → '24h' como placeholder.
    mode: enabled ? aiHoursMode.value : '24h',
    timezone: aiTimezone.value,
    schedule: schedule.value.map(day => {
      const [openHour, openMinutes] = day.openTime.split(':');
      const [closeHour, closeMinutes] = day.closeTime.split(':');
      return {
        day_of_week: day.day_of_week,
        closed_all_day: !day.open,
        open_all_day: false,
        open_hour: Number(openHour),
        open_minutes: Number(openMinutes),
        close_hour: Number(closeHour),
        close_minutes: Number(closeMinutes),
      };
    }),
  };

  isSavingHours.value = true;
  try {
    await store.dispatch('captainInboxes/updateAiHours', {
      inboxId: props.inbox.id,
      assistantId: route?.params?.assistantId,
      aiHours,
    });
    useAlert('Horário de atuação da IA salvo.');
  } catch (error) {
    useAlert(error?.message || 'Não foi possível salvar o horário da IA.');
  } finally {
    isSavingHours.value = false;
  }
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- Textos em português direto no template são dívida anterior a 02/09/2026;
     a fatia 2 das abas só acrescentou a confirmação de modo (já no i18n). -->
<template>
  <CardLayout>
    <div class="flex flex-col w-full gap-3">
      <div class="flex justify-between w-full gap-1">
        <span
          class="text-base text-n-slate-12 line-clamp-1 flex items-center gap-2"
        >
          <span :class="icon" />
          {{ inboxName }}
        </span>
        <div class="flex items-center gap-2">
          <Policy
            v-on-clickaway="() => toggleDropdown(false)"
            :permissions="['administrator']"
            class="relative flex items-center group"
          >
            <Button
              icon="i-ri-more-2-line"
              color="slate"
              size="xs"
              class="rounded-md group-hover:bg-n-alpha-2"
              @click="toggleDropdown()"
            />
            <DropdownMenu
              v-if="showActionsDropdown"
              :menu-items="menuItems"
              class="mt-1 ltr:right-0 rtl:left-0 top-full"
              @action="handleAction($event)"
            />
          </Policy>
        </div>
      </div>

      <div class="flex flex-col gap-1.5 pt-3 border-t border-n-weak">
        <div class="flex items-center justify-between gap-2">
          <span class="text-sm font-medium text-n-slate-11">
            Modo da IA nesta caixa
            <span v-if="!isAdmin" class="text-xs font-normal text-n-slate-10">
              (só administradores podem alterar)
            </span>
          </span>
          <select
            v-model="mode"
            :disabled="!isAdmin || isSavingMode"
            :title="
              isAdmin
                ? ''
                : 'Apenas administradores podem alterar o modo da IA.'
            "
            class="rounded-xl border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 text-sm font-medium text-n-slate-12 outline-none focus:border-n-brand disabled:opacity-60 disabled:cursor-not-allowed"
            @change="onModeChange"
          >
            <option
              v-for="opt in AI_MODE_OPTIONS"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
        <span v-if="modeDescription" class="text-xs text-n-slate-11">
          {{ modeDescription }}
        </span>
      </div>

      <div class="flex flex-col gap-2 pt-3 border-t border-n-weak">
        <button
          type="button"
          class="flex items-center justify-between gap-2 text-sm font-medium text-n-slate-11"
          @click="showHours = !showHours"
        >
          <span class="flex items-center gap-1">
            Horário de atuação da IA
            <span class="text-xs font-normal text-n-slate-10"
              >· {{ hoursSummary }}</span
            >
          </span>
          <span
            :class="
              showHours ? 'i-ri-arrow-up-s-line' : 'i-ri-arrow-down-s-line'
            "
          />
        </button>

        <div v-if="showHours" class="flex flex-col gap-3">
          <p class="text-xs text-n-slate-10">
            Independente do expediente dos atendentes — controla quando a IA
            responde sozinha nesta caixa.
          </p>
          <label
            v-for="opt in HOURS_MODE_OPTIONS"
            :key="opt.value"
            class="flex items-start gap-2 text-sm text-n-slate-12"
            :class="{ 'opacity-60': !isAdmin }"
          >
            <input
              v-model="aiHoursMode"
              type="radio"
              :value="opt.value"
              :disabled="!isAdmin"
              class="mt-1"
            />
            <span>
              <span class="font-medium">{{ opt.label }}</span>
              <span class="block text-xs text-n-slate-10">{{
                opt.description
              }}</span>
            </span>
          </label>

          <div v-if="needsSchedule" class="flex flex-col gap-2 pl-1">
            <label
              class="flex items-center justify-between gap-2 text-sm text-n-slate-11"
            >
              Fuso horário
              <select
                v-model="aiTimezone"
                :disabled="!isAdmin"
                class="rounded-xl border border-n-weak bg-n-alpha-1 px-2 py-1 text-sm text-n-slate-12 outline-none focus:border-n-brand disabled:opacity-60"
              >
                <option v-for="tz in TIMEZONES" :key="tz" :value="tz">
                  {{ tz }}
                </option>
              </select>
            </label>
            <div class="flex flex-col gap-1">
              <div
                v-for="day in schedule"
                :key="day.day_of_week"
                class="flex items-center gap-2 text-sm"
              >
                <label class="flex items-center gap-1.5 w-16">
                  <input
                    v-model="day.open"
                    type="checkbox"
                    :disabled="!isAdmin"
                  />
                  <span class="text-n-slate-11">{{
                    DAY_LABELS[day.day_of_week]
                  }}</span>
                </label>
                <template v-if="day.open">
                  <input
                    v-model="day.openTime"
                    type="time"
                    :disabled="!isAdmin"
                    class="rounded border border-n-weak bg-n-alpha-1 px-1.5 py-1 text-sm text-n-slate-12 disabled:opacity-60"
                  />
                  <span class="text-n-slate-10">–</span>
                  <input
                    v-model="day.closeTime"
                    type="time"
                    :disabled="!isAdmin"
                    class="rounded border border-n-weak bg-n-alpha-1 px-1.5 py-1 text-sm text-n-slate-12 disabled:opacity-60"
                  />
                </template>
                <span v-else class="text-xs text-n-slate-10">Fechado</span>
              </div>
            </div>
            <p class="text-xs text-n-slate-10">
              A janela pode cruzar a meia-noite (ex.: 20:00–08:00 cobre a
              madrugada).
            </p>
          </div>

          <button
            v-if="isAdmin"
            type="button"
            :disabled="isSavingHours"
            class="self-start rounded-xl border border-n-weak bg-n-alpha-2 px-3 py-1.5 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-3 disabled:opacity-60 disabled:cursor-not-allowed"
            @click="onSaveHours"
          >
            {{ isSavingHours ? 'Salvando…' : 'Salvar horário' }}
          </button>
        </div>
      </div>
    </div>
    <Dialog
      ref="confirmaModoDialog"
      type="alert"
      :title="t('CAPTAIN.ONDE_ATUA.CONFIRMAR_AUTONOMO.TITULO')"
      :description="
        t('CAPTAIN.ONDE_ATUA.CONFIRMAR_AUTONOMO.TEXTO', { caixa: inbox.name })
      "
      :confirm-button-label="
        t('CAPTAIN.ONDE_ATUA.CONFIRMAR_AUTONOMO.CONFIRMAR')
      "
      :cancel-button-label="t('CAPTAIN.ONDE_ATUA.CONFIRMAR_AUTONOMO.CANCELAR')"
      @confirm="confirmarModo"
      @close="cancelarModo"
    />
  </CardLayout>
</template>
